import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

interface UseElementVisibilityOptions {
  enabled?: boolean;
  isVisible?: (entry: IntersectionObserverEntry) => boolean;
}

const defaultIsVisible = (entry: IntersectionObserverEntry) =>
  entry.isIntersecting;

export function useElementVisibility(
  elementRef: RefObject<Element | null>,
  {
    enabled = true,
    isVisible = defaultIsVisible,
  }: UseElementVisibilityOptions = {},
) {
  const [isVisibleState, setIsVisibleState] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsVisibleState(false);
      return;
    }

    const element = elementRef.current;

    if (!element || typeof IntersectionObserver !== 'function') {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry) {
        return;
      }

      setIsVisibleState(isVisible(entry));
    });

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [elementRef, enabled, isVisible]);

  return isVisibleState;
}
